/**
 * ContentReviewPanel — sidebar panel for content review (the canonical content
 * drawer; parallels ScriptReviewPanel in ../ScriptReview).
 *
 * Used by: ContentTab (interactive review) and ConfirmedTab (read-only audit).
 * Always renders as a full-height right-side panel; no modal mode.
 *
 * Layout: video + metadata | AI review tabs | optional thread column.
 * ChatThread is the shared common component — same as in ScriptReviewPanel
 * and ReviewThreadDrawer (creator side-panel).
 */
import React from 'react';
import type { AssignedProduct } from '@discovr/types';
import { Sheet, SheetContent, SheetTitle } from '../../ui/sheet';
import { Check, ExternalLink, Loader2, MessageCircle, Video, X } from '../../../icons';
import { Button } from '../../ui/button';
import { cn } from '../../../lib/utils';
import { ChatThread } from '../ChatThread';
import { ReviewStatusPill, reviewStatusTone } from '../ReviewStatusPill';
import { UnreadBadge } from '../UnreadBadge';
import { ContentResultTabs } from './ContentResultTabs';
import type { ContentReviewBody, GeoAiEnvelope } from './types';
import type { ReviewThreadEntry } from '../../../lib/models';
import { SharePopover } from '../../../campaign-detail/share/SharePopover';
import { useCampaignShare } from '../../../campaign-detail/share/useCampaignShare';
import { purposeForPanel, type ShortShareInput } from '../../../campaign-detail/share/shortLink';
import type { CampaignSearch } from '../../../campaign-detail/search';
import { usePublicView } from '../../../campaign-detail/publicView';

// ─── Video embed helper ───────────────────────────────────────────────────────

function youtubeEmbedId(url: string): string | null {
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  return m?.[1] ?? null;
}

function isYouTubeShorts(url: string): boolean {
  return /youtube\.com\/shorts\//.test(url);
}

function vimeoEmbedId(url: string): string | null {
  const m = url.match(/vimeo\.com\/(?:video\/|channels\/[\w-]+\/|groups\/[\w-]+\/forum\/topic:|)(\d+)/);
  return m?.[1] ?? null;
}

function VideoEmbed({ url, title }: { url: string; title: string }) {
  const yid = youtubeEmbedId(url);
  if (yid) {
    const isShorts = isYouTubeShorts(url);
    return isShorts ? (
      <div className="flex w-full justify-center">
        <div className="overflow-hidden rounded-sm bg-black" style={{ width: 315, height: 560 }}>
          <iframe
            title={title}
            src={`https://www.youtube.com/embed/${yid}`}
            style={{ width: '100%', height: '100%' }}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </div>
    ) : (
      <div className="aspect-video w-full overflow-hidden rounded-sm bg-black">
        <iframe
          title={title}
          src={`https://www.youtube.com/embed/${yid}`}
          className="size-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }

  const vid = vimeoEmbedId(url);
  if (vid) {
    return (
      <div className="aspect-video w-full overflow-hidden rounded-sm bg-black">
        <iframe
          title={title}
          src={`https://player.vimeo.com/video/${vid}`}
          className="size-full"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }

  const looksLikeDirectVideo = /\.(mp4|webm|ogg|mov|m4v)(\?|$)/i.test(url) || url.includes('blob:');
  if (looksLikeDirectVideo) {
    return (
      <div className="aspect-video w-full overflow-hidden rounded-sm bg-black ring-1 ring-white/10">
        <video src={url} controls className="size-full" title={title} controlsList="nodownload" />
      </div>
    );
  }

  return (
    <div className="flex min-h-[240px] flex-col items-center justify-center gap-4 overflow-hidden rounded-xl border border-border bg-muted p-8 text-center">
      <div className="flex size-16 items-center justify-center rounded-2xl bg-card text-muted-foreground ring-1 ring-ring">
        <ExternalLink size={24} />
      </div>
      <div className="max-w-[240px] space-y-1.5">
        <h4 className="text-body-sm-semibold text-foreground">External Content Preview</h4>
        <p className="text-caption-regular leading-relaxed text-muted-foreground">
          This content is hosted on an external platform that restricts direct embedding.
        </p>
      </div>
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="mt-2 inline-flex items-center gap-2 rounded-xl bg-(--brand-500) px-6 py-2.5 text-body-sm-semibold text-white transition-all hover:bg-(--brand-600)"
      >
        <ExternalLink size={16} /> Open Link in New Tab
      </a>
    </div>
  );
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ContentReviewPanelProps {
  open: boolean;
  onClose: () => void;
  title: string;
  creatorName: string;
  creatorAvatarUrl?: string;
  creatorHandle?: string;

  /** Products assigned to this creator (Multi-Product Campaigns), resolved from
   *  ContentDetail.assigned_products. Rendered as read-only "Featuring" chips in
   *  the header — identical for brand and admin. */
  assignedProducts?: AssignedProduct[];
  /** Campaign product-catalog size — gates the Featuring chips to >= 2 products
   *  (0-1 product campaigns render no chips, exactly as before). */
  campaignProductCount?: number;

  /** Roster entry id — the id the Regenerate/Rephrase/Select GEO mutations
   *  post to. Threaded to ContentResultTabs (as `geoEntryId`); this panel
   *  makes no network calls of its own with it. */
  entryId?: string;
  /** GEO finalize envelope (ContentDetail.geo_finalize) — real chapter
   *  timestamps + a publish-ready description stitched onto the uploaded
   *  video's transcript. Threaded straight into ContentResultTabs' GEO tab;
   *  this panel makes no network calls of its own. Mirrors ScriptReviewPanel's
   *  `geoAnalysis` prop. */
  geoAnalysis?: GeoAiEnvelope;
  /** Show the "GEO Optimized" tab. Gate to YouTube campaigns at the call
   *  site — mirrors ScriptReviewPanel's `showGeo`. */
  showGeo?: boolean;
  /** Regenerate the whole GEO finalize pack — wired by the caller (which owns
   *  the actual mutation); this panel just threads the callback through to
   *  ContentResultTabs. Hidden there when `geoReadOnly` or when this prop
   *  isn't provided at all. May return a Promise — ContentResultTabs' GEO tab
   *  awaits it to drive a local busy/spinner state. */
  onRegenerateGeoFinalize?: () => void | Promise<void>;
  /** Rephrase a single finalized element — wired by the caller, same pattern
   *  as `onRegenerateGeoFinalize`. */
  onRephraseGeoElement?: (element: 'chapters' | 'description') => void | Promise<void>;
  /** Save the FULL next finalize-selection array — wired by the caller, same
   *  pattern as `onRegenerateGeoFinalize`. ContentResultTabs' GEO tab computes
   *  the next array from its own local selection state and passes the whole
   *  thing, not just the toggled key. */
  onSaveGeoSelection?: (selected: string[]) => void | Promise<void>;
  /** Read-only GEO surface (Confirmed/finalized tab): GEO finalize still
   *  auto-runs on open, but the manual Re-finalize button is hidden.
   *  Defaults to false so live review callers are unaffected. */
  geoReadOnly?: boolean;

  /** Platform username for the admin share deep link (distinct from the
   *  presentational `creatorHandle`, which may be the display name). */
  shareCreatorUsername?: string;
  /** Override the tab embedded in the share URL (defaults to 'content').
   *  Pass 'confirmed' when rendering this panel from the Finalized tab. */
  shareTab?: NonNullable<CampaignSearch['tab']>;
  /** Override the panel value embedded in the share URL (defaults to
   *  'content-review' for the live review surface). Pass 'content-finalized'
   *  when rendering this panel from the Finalized tab so a shared link routes
   *  back to the read-only surface — not the live workflow review. */
  sharePanel?: 'content-review' | 'content-finalized';
  statusLabel: string;
  updatedLabel: string;
  costLabel?: string;
  // Video
  videoUrl?: string;
  videoSecondaryUrl?: string;
  /** True when the content URL is a Drive link and the S3 video isn't ready yet.
   *  Shows a processing spinner in place of the video embed. */
  videoProcessing?: boolean;
  // Optional content below the video
  leftExtras?: React.ReactNode;
  // Approved ribbon
  showApprovedBanner?: boolean;
  approvedBannerText?: string;
  // Review action (Approve button in header)
  canReview: boolean;
  reviewBusy?: boolean;
  isApproved?: boolean;
  onApprove: () => void;
  // Thread column (right panel toggle)
  thread?: ReviewThreadEntry[];
  unreadCount?: number;
  canAddComment?: boolean;
  /** Thread is locked (approved -> read-only). Forwarded to ChatThread so
   *  Edit/Delete gate on the LOCK rather than on `canAddComment`, which at some
   *  call sites also encodes "is a post handler wired?" and would otherwise
   *  strip both affordances from an open thread. Omitted: ChatThread falls back
   *  to `canAddComment === false`. */
  locked?: boolean;
  addCommentBusy?: boolean;
  onAddComment?: (body: string, replyTo?: string) => void | Promise<void>;
  onEditMessage?: (id: string, body: string) => void | Promise<void>;
  onDeleteMessage?: (id: string) => void | Promise<void>;
  /** True only for the Super Admin tier — lets ChatThread's delete
   *  affordance bypass the locked-thread gate (server permission flags
   *  already scope who may actually delete what). */
  viewerIsSuperAdmin?: boolean;
  viewerActor?: string;
  // AI review result
  aiAnalysis?: {
    status: 'pending' | 'complete' | 'failed';
    data?: Record<string, unknown>;
    error?: string;
  };
  /** 'admin' shows real failure states; 'brand' shows softened "still preparing" banner. */
  audience?: 'brand' | 'admin';
  /** Called when the user expands the thread section. Use to fire mark-read. */
  onThreadOpen?: () => void;
  /** Called whenever the thread column toggles (open or close). Use to mirror
   *  the state to `?thread=open` so refresh restores the panel identically. */
  onThreadOpenChange?: (open: boolean) => void;
  /** Deep-link hint: start with the thread column already expanded
   *  (e.g. `?panel=content-review&thread=open`). Falls back to the existing
   *  "open when the thread already has messages" behaviour. */
  defaultThreadOpen?: boolean;
  /** D5: when true, the conversation toggle button itself is not rendered
   *  at all (not just disabled) — used when a public grant's
   *  allow_chat_readonly was off at mint time, so there is genuinely
   *  nothing to show, not an empty state to reveal. Defaults false — every
   *  existing authed caller is unaffected. */
  hideThreadToggle?: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ContentReviewPanel({
  open,
  onClose,
  title,
  creatorName,
  creatorAvatarUrl,
  creatorHandle,
  assignedProducts,
  campaignProductCount,
  entryId,
  geoAnalysis,
  showGeo,
  onRegenerateGeoFinalize,
  onRephraseGeoElement,
  onSaveGeoSelection,
  geoReadOnly,
  shareCreatorUsername,
  shareTab: _shareTab = 'content',
  sharePanel = 'content-review',
  statusLabel,
  updatedLabel,
  costLabel,
  videoUrl,
  videoSecondaryUrl,
  videoProcessing = false,
  leftExtras,
  showApprovedBanner,
  approvedBannerText,
  canReview,
  reviewBusy,
  isApproved,
  onApprove,
  thread,
  unreadCount,
  canAddComment,
  locked,
  addCommentBusy,
  onAddComment,
  onEditMessage,
  onDeleteMessage,
  viewerIsSuperAdmin,
  viewerActor = 'brand',
  aiAnalysis,
  audience = 'admin',
  onThreadOpen,
  onThreadOpenChange,
  defaultThreadOpen = false,
  hideThreadToggle = false,
}: ContentReviewPanelProps) {
  const { canShare, makeShortUrl } = useCampaignShare();
  const { isPublicView } = usePublicView();
  const shareInput: ShortShareInput | null = shareCreatorUsername
    ? {
        kind: 'creator',
        username: shareCreatorUsername,
        purpose: purposeForPanel(sharePanel) ?? 'content',
        // No `sub` here — see ScriptReviewPanel.tsx's shareInput comment
        // (2026-08-05 "one link per review panel" decision): a content grant
        // already returns the full panel from one complete payload.
      }
    : null;
  const [threadOpen, setThreadOpen] = React.useState<boolean>(
    () => defaultThreadOpen || (Array.isArray(thread) && thread.length > 0),
  );
  const [threadDraft, setThreadDraft] = React.useState('');
  const threadDraftRef = React.useRef<HTMLTextAreaElement>(null);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    if (open) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || typeof document === 'undefined') return null;

  // Plain inline style, not a Tailwind arbitrary-value class — Tailwind's JIT
  // never generates a rule for a nested min(max(...),calc(...)) class (no
  // matching rule appears in any loaded stylesheet), so the panel silently
  // fell back to auto/content width. sm:max-w-none max-[992px]:w-full! below
  // still wins on narrow viewports via !important, which beats inline style.
  // Fixed-px floors (not just vw-percentage) so the tab row — up to 7 tabs
  // incl. GEO Optimized — always has room: video column (420) + tabs column
  // (820) = 1240 min; +360 chat column when the thread is open.
  const panelWidth = threadOpen
    ? { width: 'min(max(72vw, 1600px), calc(100vw - 3rem))' }
    : { width: 'min(max(72vw, 1240px), calc(100vw - 3rem))' };

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent
        side="right"
        showCloseButton={false}
        aria-label={title}
        style={panelWidth}
        className="min-h-0 gap-0 border-l border-border bg-card p-0 transition-[width] duration-300 ease-in-out sm:max-w-none max-[992px]:w-full!"
        // Inert everywhere except the public share shell, which reads this
        // attribute in its click-capture allow-list (PublicCampaignShell.tsx)
        // to let every read-only interaction inside THIS granted panel work
        // normally on a public link — see that file's header. Radix's own
        // overlay-click/Escape dismissal (onOpenChange above) is unaffected
        // either way — it never goes through React's click-capture at all.
        data-public-allowed-subtree=""
      >
        <SheetTitle className="sr-only">{title}</SheetTitle>

        {/* Full-width top bar — creator info (left) + actions (right). Stretches
            as the panel widens; the conversation spawns in from the right below
            it. No section labels (the drawer is self-evident). */}
        <div className="flex h-[70px] shrink-0 items-center gap-3 border-b border-border-subtle bg-card px-5">
              <div
                className="size-9 shrink-0 rounded-full border border-border bg-muted bg-cover bg-center"
                style={creatorAvatarUrl ? { backgroundImage: `url(${creatorAvatarUrl})` } : undefined}
                aria-hidden
              />
              <div className="flex min-w-0 flex-col gap-0.5">
                <div className="flex items-center gap-2">
                  <span className="truncate text-body-sm-regular/5 font-medium text-foreground">{creatorName}</span>
                  {statusLabel ? <ReviewStatusPill label={statusLabel} tone={reviewStatusTone(statusLabel)} /> : null}
                </div>
                {creatorHandle && (
                  <span className="truncate text-caption-regular/4 font-medium text-muted-foreground">{creatorHandle}</span>
                )}
              </div>
              <div className="ml-auto flex items-center gap-2">
                {isApproved ? (
                  <Button
                    variant="default"
                    type="button"
                    disabled
                    className="inline-flex h-10 items-center gap-1.5 rounded-full bg-green-600 px-5 text-sm font-medium text-white opacity-100 cursor-default"
                  >
                    Approved
                  </Button>
                ) : canReview && (
                  <Button
                    variant="default"
                    type="button"
                    onClick={onApprove}
                    disabled={reviewBusy}
                    className="inline-flex h-10 items-center gap-1.5 rounded-full bg-foreground px-5 text-sm font-medium text-white transition-colors hover:bg-foreground/90 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {reviewBusy ? <Loader2 size={12} className="animate-spin" /> : null}
                    Approve
                  </Button>
                )}
                {!hideThreadToggle && (
                  <Button
                    variant="outline"
                    size="icon"
                    type="button"
                    onClick={() => {
                      const opening = !threadOpen;
                      setThreadOpen(opening);
                      if (opening) onThreadOpen?.();
                      onThreadOpenChange?.(opening);
                    }}
                    className={cn(
                      'relative size-10 shrink-0 rounded-full border p-0',
                      threadOpen
                        ? 'border-foreground bg-muted text-foreground hover:bg-muted'
                        : 'border-border bg-card text-foreground hover:bg-accent',
                    )}
                    aria-pressed={threadOpen}
                    aria-label="Conversation"
                  >
                    <MessageCircle size={16} />
                    {unreadCount ? (
                      <span className="absolute -right-1 -top-1">
                        <UnreadBadge count={unreadCount} variant="inline" toneClassName="bg-rose-500" />
                      </span>
                    ) : null}
                  </Button>
                )}
                {/* `!isPublicView`: keeps SharePopover's authed useShareGrants
                    query unreachable from the public shell by construction —
                    see the same guard in DrawerHeader. */}
                {canShare && shareCreatorUsername && !isPublicView && (
                  <SharePopover
                    input={shareInput}
                    getUrl={() => (shareInput ? makeShortUrl(shareInput) : null)}
                  />
                )}
                {/* Public (no-login) share view: the panel IS the shared
                    surface — nothing to close back to, so hide the control. */}
                {!isPublicView && (
                  <Button
                    variant="ghost"
                    size="icon"
                    type="button"
                    onClick={onClose}
                    className="inline-flex size-9 items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-accent"
                    aria-label="Close"
                  >
                    <X size={18} />
                  </Button>
                )}
              </div>
        </div>

        {/* Body: review (left) | conversation (right), divided by a vertical
            border. The conversation spawns in from the right, pushing the review
            content to the left. */}
        <div className="flex min-h-0 flex-1 overflow-x-auto overflow-y-hidden">
          {/* Review body: video + metadata | AI review tabs. Always side-by-side.
              Each column is independently bounded — only the AI-tabs column scrolls.
              The video column is a FIXED 420px — it never compresses. The tabs
              column takes the remaining space and can shrink freely; its own tab
              row (not this whole column) scrolls horizontally when the tab labels
              don't fit, so the video content is never squeezed. */}
          <div className="grid min-h-0 flex-1 grid-cols-[420px_minmax(0,1fr)] divide-x divide-border">
              {/* Video + metadata — no scroll; stays fixed while AI tabs scroll */}
              <div className="min-h-0 overflow-hidden bg-card">
                <div className="flex flex-col gap-5 px-6 pb-6 pt-5">
                  <div className="space-y-2">
                    {videoProcessing ? (
                      <div className="flex min-h-[240px] flex-col items-center justify-center gap-4 rounded-xl border border-border bg-muted p-8 text-center">
                        <div className="flex size-16 items-center justify-center rounded-2xl bg-card ring-1 ring-ring">
                          <Video size={28} className="text-muted-foreground" />
                        </div>
                        <div className="space-y-1.5">
                          <h4 className="text-body-sm-semibold text-foreground">Processing video…</h4>
                          <p className="text-caption-regular leading-relaxed text-muted-foreground">
                            The video is being prepared for review. This usually takes a minute.
                          </p>
                        </div>
                        <Loader2 size={20} className="animate-spin text-(--brand-500)" />
                      </div>
                    ) : videoUrl ? (
                      <VideoEmbed url={videoUrl} title={`${creatorName} content`} />
                    ) : (
                      <div className="rounded-xl border border-dashed border-border bg-muted px-4 py-12 text-center text-body-sm-regular text-muted-foreground">
                        No video URL yet. Add a content URL to preview here.
                      </div>
                    )}
                    {videoSecondaryUrl && videoSecondaryUrl !== videoUrl && (
                      <a
                        href={videoSecondaryUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex w-fit items-center gap-1.5 text-caption-medium text-muted-foreground hover:text-violet-700"
                      >
                        <ExternalLink size={12} className="text-muted-foreground" />
                        Open alternate link
                      </a>
                    )}
                  </div>

                  {/* Metadata strip — Figma KPI 1: p-6, border, rounded-lg */}
                  <div className={cn('grid justify-between rounded-lg border border-border bg-card p-6', costLabel ? 'grid-cols-3' : 'grid-cols-2')}>
                    <MetaField label="Status">
                      <span className={cn(
                        'inline-flex items-center rounded-full px-3 py-1.5 text-caption-medium',
                        /live|approved/i.test(statusLabel)
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'bg-[rgba(108,9,247,0.10)] text-[#6C09F7]',
                      )}>
                        {statusLabel}
                      </span>
                    </MetaField>
                    <MetaField label="Updated">
                      <span className="text-caption-medium tabular-nums text-foreground">{updatedLabel}</span>
                    </MetaField>
                    {costLabel && (
                      <MetaField label="Cost">
                        <span className="text-caption-medium tabular-nums text-foreground">{costLabel}</span>
                      </MetaField>
                    )}
                  </div>

                  {/* leftExtras (ContentRowDetail, embed) owns its own border and
                      returns null when it has nothing to show — so no empty card. */}
                  {leftExtras}

                  {showApprovedBanner && (
                    <div className="flex items-center gap-2.5 rounded-xl border border-emerald-200/80 bg-emerald-50/90 px-4 py-3">
                      <Check size={18} className="shrink-0 text-emerald-600" strokeWidth={2.5} />
                      <span className="text-body-sm-semibold text-emerald-900">
                        {approvedBannerText ?? 'Approved'}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* AI review tabs — scrolls independently within its bounded column.
                  No overflow/pb-5 here: ContentResultTabs now handles its own
                  internal split (shrink-0 tab bar + a scrolling region below
                  it, with the bottom padding living inside that region), so
                  its vertical scrollbar runs alongside the tab content only,
                  not the tab bar above it. */}
              <div className="flex h-full min-h-0 flex-col bg-card">
                <ContentResultTabs
                  data={aiAnalysis?.data as ContentReviewBody | undefined}
                  aiStatus={aiAnalysis?.status}
                  aiError={aiAnalysis?.error}
                  audience={audience}
                  geoEntryId={entryId}
                  geoAnalysis={geoAnalysis}
                  showGeo={showGeo}
                  onRegenerateGeoFinalize={onRegenerateGeoFinalize}
                  onRephraseGeoElement={onRephraseGeoElement}
                  onSaveGeoSelection={onSaveGeoSelection}
                  geoReadOnly={geoReadOnly}
                  assignedProducts={assignedProducts}
                  campaignProductCount={campaignProductCount}
                />
              </div>
            </div>

          {/* Chat column — always mounted so closing animates out smoothly.
              overflow-hidden + width transition slides it in from the right.
              Inner div holds fixed width so ChatThread doesn't reflow mid-animation. */}
          <div
            className={cn(
              'shrink-0 h-full overflow-hidden bg-card transition-[width] duration-300 ease-in-out',
              threadOpen ? 'w-[360px] border-l border-border' : 'w-0',
            )}
          >
            <div className="flex h-full w-[360px] min-h-0 flex-col">
              <ChatThread
                entries={thread}
                canAddComment={canAddComment}
                locked={locked}
                addCommentBusy={addCommentBusy}
                onAddComment={
                  onAddComment
                    ? ({ body, replyTo }) => onAddComment(body, replyTo)
                    : undefined
                }
                onEditMessage={onEditMessage}
                onDeleteMessage={onDeleteMessage}
                viewerIsSuperAdmin={viewerIsSuperAdmin}
                enableReplies
                outgoingActor={viewerActor}
                draft={threadDraft}
                setDraft={setThreadDraft}
                textareaRef={threadDraftRef}
              />
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─── Internal helper ──────────────────────────────────────────────────────────

function MetaField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <dt className="text-caption-regular/4 font-medium text-muted-foreground">{label}</dt>
      <dd className="flex min-h-7 items-center">{children}</dd>
    </div>
  );
}
